import { FC, useRef } from 'react';
import { NavbarMenuItem } from './NavbarMenuItem';
import MenuRulesPoliciesIcon from '@/assets/images/menu__rules-policies.svg';
import { Box, Collapse, List, ListItemButton, ListItemText, Stack } from '@mui/material';
import { navbarMenuItemMobileText, mobileMenuItemSectionButton } from './NavbarMenuItem.styles';
import { theme } from '@theme';
import { OpensInNewTab } from '@barracuda/shared';
import Img from '@barracuda/shared/components/Primitive/Img';
import ArrowUp from '@/assets/images/menu__arrow-up.svg';
import ArrowDown from '@/assets/images/menu__arrow-down.svg';
import { NIcon } from '@barracuda/shared/components/Nemo/NIcon/NIcon';

export interface MenuItemType {
  title: string;
  items: MenuSubItemType[];
  link?: string;
}

export interface MenuSubItemType {
  link: string;
  title: string;
  icon: string | JSX.Element;
  target?: string;
}

const toMenuId = (title: string, prefix: string) =>
  `${prefix}-${title
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '')}`;
const scoreComparisonURI: string = `${process.env.REACT_APP_PESC_URL}${process.env.REACT_APP_PESC_SCORE_COMPARISON_PDF}`;
const iconColor = theme.palette.common.darkGrey12;

const desktopItems: MenuItemType[] = [
  {
    title: 'Students',
    items: [
      {
        link: '/test-format',
        title: 'Test Format',
        icon: <NIcon name='presentation-chart' size='md' color={iconColor} />
      },
      {
        link: '/for-students/institutions',
        title: 'Institutions list',
        icon: <NIcon name='graduation-cap' size='md' color={iconColor} />
      },
      { link: '/policies-rules', title: 'Test Rules and Policies', icon: MenuRulesPoliciesIcon },
      {
        link: scoreComparisonURI,
        target: '_blank',
        title: 'Score guide',
        icon: <NIcon name='file-pdf' size='md' color={iconColor} />
      }
    ]
  },
  {
    title: 'Institutions',
    items: [
      {
        link: '/for-institutions',
        title: 'Accept the test',
        icon: <NIcon name='lightbulb' size='md' color={iconColor} />
      },
      {
        link: '/test-format',
        title: 'Test Format',
        icon: <NIcon name='presentation-chart' size='md' color={iconColor} />
      },
      {
        link: scoreComparisonURI,
        target: '_blank',
        title: 'Score guide',
        icon: <NIcon name='file-pdf' size='md' color={iconColor} />
      }
    ]
  }
];

const mobileItems: MenuItemType[] = [{ title: 'Homepage', items: [], link: '/' }, ...desktopItems];

export const renderDesktopItems = () =>
  desktopItems.map((item) => (
    <NavbarMenuItem title={item.title} items={item.items} key={`${item.title}`} />
  ));

interface MobileMenuItemSectionProps {
  item: MenuItemType;
  index: number;
  isExpanded: boolean;
  handleClick: (item: MenuItemType | MenuSubItemType) => void;
}

const MobileMenuItemSection: FC<MobileMenuItemSectionProps> = ({
  item,
  index,
  isExpanded,
  handleClick
}) => {
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const firstSubmenuItemRef = useRef<HTMLDivElement>(null);
  const hasSubItems = !!item.items.length;
  const menuButtonId = toMenuId(item.title, 'mobile-menu-button');
  const submenuId = toMenuId(item.title, 'mobile-submenu');
  const firstSubmenuItemId = `${submenuId}-first-item`;

  const focusFirstSubmenuItem = () => {
    firstSubmenuItemRef.current?.focus();
  };

  return (
    <Stack key={`button-container-${index}`}>
      <ListItemButton
        ref={menuButtonRef}
        id={menuButtonId}
        aria-expanded={hasSubItems ? isExpanded : undefined}
        aria-controls={hasSubItems && isExpanded ? submenuId : undefined}
        sx={mobileMenuItemSectionButton(isExpanded)}
        onClick={() => handleClick(item)}
      >
        <ListItemText sx={navbarMenuItemMobileText} primary={item.title} />
        {hasSubItems && (
          <Box component='span' aria-hidden sx={{ display: 'flex' }}>
            <Img src={isExpanded ? ArrowUp : ArrowDown} alt='' aria-hidden />
          </Box>
        )}
      </ListItemButton>
      {hasSubItems && (
        <Collapse
          id={submenuId}
          in={isExpanded}
          timeout='auto'
          unmountOnExit
          role='region'
          aria-labelledby={menuButtonId}
          onEntered={focusFirstSubmenuItem}
          onExited={() => menuButtonRef.current?.focus()}
        >
          <List component='ul' sx={{ padding: 0 }}>
            {item.items.map((subItem, subIndex) => (
              <ListItemButton
                key={`subitem-${subIndex}`}
                id={subIndex === 0 ? firstSubmenuItemId : undefined}
                ref={subIndex === 0 ? firstSubmenuItemRef : undefined}
                sx={{ padding: '16px', borderRadius: '4px' }}
                onClick={() => handleClick(subItem)}
              >
                <ListItemText
                  sx={navbarMenuItemMobileText}
                  primary={
                    <>
                      {subItem.title}
                      {subItem.target === '_blank' && <OpensInNewTab />}
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </Stack>
  );
};

interface MobileMenuItemsProps {
  activeItems: MenuItemType[];
  handleClick: (item: MenuItemType | MenuSubItemType) => void;
}

export const MobileMenuItems: FC<MobileMenuItemsProps> = ({ activeItems, handleClick }) => (
  <>
    {mobileItems.map((item, index) => (
      <MobileMenuItemSection
        key={`button-container-${index}`}
        item={item}
        index={index}
        isExpanded={activeItems.includes(item)}
        handleClick={handleClick}
      />
    ))}
  </>
);

export const renderMobileItems = (
  activeItems: MenuItemType[],
  handleClick: (item: MenuItemType | MenuSubItemType) => void
) => <MobileMenuItems activeItems={activeItems} handleClick={handleClick} />;
